import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid3x3, RefreshCw, Layers, Download } from "lucide-react";
import { toast } from "sonner";
import { Treemap, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { downloadCsv, csvButtonBase } from "@/lib/csv";

const UNIVERSES = [
  { id: "bist30", label: "BIST 30" },
  { id: "bist100", label: "BIST 100" },
];

function colorFor(pct) {
  if (pct == null) return "#2A3D66";
  const clamped = Math.max(-8, Math.min(8, pct));
  if (clamped >= 0) {
    const t = clamped / 8;
    return `rgb(${Math.round(30+(70-30)*t)},${Math.round(80+(192-80)*t)},${Math.round(60+(111-60)*t)})`;
  }
  const t = Math.abs(clamped) / 8;
  return `rgb(${Math.round(120+(229-120)*t)},${Math.round(50+(72-50)*t)},${Math.round(60+(77-60)*t)})`;
}

const CustomCell = (props) => {
  const { x, y, width, height, symbol, pct_change } = props;
  if (!symbol) return null;
  const pct = Number.isFinite(pct_change) ? pct_change : 0;
  const fill = colorFor(pct);
  const showLabel = width > 44 && height > 28;
  const showPct = width > 60 && height > 40;
  return (
    <g data-testid={`heatmap-cell-${symbol}`}>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#0F1B33" strokeWidth={2} />
      {showLabel && (
        <text x={x + width / 2} y={y + height / 2 - (showPct ? 6 : 0)} textAnchor="middle" fill="#FFFFFF"
              stroke="none" paintOrder="fill"
              fontSize={Math.min(15, Math.max(11, width / 7))} fontWeight={700} fontFamily="JetBrains Mono">
          {symbol}
        </text>
      )}
      {showPct && (
        <text x={x + width / 2} y={y + height / 2 + 14} textAnchor="middle" fill="#FFFFFF"
              stroke="none" paintOrder="fill"
              fontSize={Math.min(13, Math.max(10, width / 10))} fontWeight={600} fontFamily="JetBrains Mono" opacity={0.98}>
          {`${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
        </text>
      )}
    </g>
  );
};

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const up = (d.pct_change ?? 0) >= 0;
  return (
    <div className="bg-bg-panel border border-bg-border rounded-lg px-3 py-2 shadow-xl text-xs">
      <div className="font-mono font-bold text-text-primary">{d.symbol}</div>
      <div className="text-text-secondary">{d.name}</div>
      {d.sector && <div className="text-[10px] text-brand-blueLight mt-0.5">{d.sector}</div>}
      <div className="flex items-center gap-3 mt-1">
        <span className="text-text-primary font-mono">₺{d.close?.toFixed(2)}</span>
        <span className={`font-mono ${up ? "text-signal-up" : "text-signal-down"}`}>
          {up ? "+" : ""}{d.pct_change?.toFixed(2)}%
        </span>
      </div>
    </div>
  );
};

export default function HeatmapPage() {
  const [universe, setUniverse] = useState("bist30");
  const [view, setView] = useState("treemap"); // treemap | sectors
  const [data, setData] = useState(null);
  const [sectorData, setSectorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get(`/heatmap?universe=${universe}`),
        api.get(`/sectors?universe=${universe}`),
      ]);
      setData(t.data); setSectorData(s.data);
    } catch { toast.error("Isı haritası yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [universe]);

  const exportCsv = () => {
    if (view === "treemap" && data) {
      downloadCsv("ztan_heatmap.csv", data.items, ["symbol","name","sector","close","pct_change","size"]);
    } else if (view === "sectors" && sectorData) {
      const rows = sectorData.sectors.map((s) => ({
        sector: s.sector, count: s.count,
        avg_pct: s.avg_pct.toFixed(2),
        up: s.up, down: s.down,
        top_gainer: s.top_gainer ? `${s.top_gainer.symbol} (${s.top_gainer.pct_change.toFixed(2)}%)` : "",
        top_loser: s.top_loser ? `${s.top_loser.symbol} (${s.top_loser.pct_change.toFixed(2)}%)` : "",
      }));
      downloadCsv("ztan_sektor_ozeti.csv", rows, ["sector","count","avg_pct","up","down","top_gainer","top_loser"]);
    }
  };

  return (
    <div className="space-y-6" data-testid="heatmap-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary ztan-title flex items-center gap-2">
            <Grid3x3 size={22} className="text-brand-blue" /> BIST Isı Haritası
          </h1>
          <p className="text-sm text-text-secondary mt-1">Kutu boyutu ≈ günlük işlem hacmi · renk ≈ günlük % değişim</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-bg-panel p-1 rounded-lg border border-bg-border">
            <button data-testid="heatmap-view-treemap" onClick={() => setView("treemap")}
              className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-1.5 ${view === "treemap" ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"}`}>
              <Grid3x3 size={12} /> Treemap
            </button>
            <button data-testid="heatmap-view-sectors" onClick={() => setView("sectors")}
              className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-1.5 ${view === "sectors" ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"}`}>
              <Layers size={12} /> Sektörler
            </button>
          </div>
          <div className="flex items-center gap-1 bg-bg-panel p-1 rounded-lg border border-bg-border">
            {UNIVERSES.map((u) => (
              <button key={u.id} data-testid={`heatmap-universe-${u.id}`} onClick={() => setUniverse(u.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${universe === u.id ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"}`}>{u.label}</button>
            ))}
          </div>
          <button data-testid="heatmap-csv" onClick={exportCsv} className={csvButtonBase()}>
            <Download size={12} /> CSV
          </button>
          <button data-testid="heatmap-refresh" onClick={load}
            className="bg-bg-hover text-text-primary border border-bg-border px-3 py-1.5 rounded-lg text-xs hover:bg-bg-border flex items-center gap-1.5">
            <RefreshCw size={12} /> Yenile
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 flex justify-center"><div className="radar-loader" /></div>
      ) : view === "treemap" ? (
        !data || data.items.length === 0 ? (
          <div className="bg-bg-panel border border-bg-border rounded-xl p-16 text-center"><div className="text-text-primary font-semibold">Veri yok</div></div>
        ) : (
          <>
            <div className="bg-bg-panel border border-bg-border rounded-xl p-2 sm:p-3" style={{ height: 640, minWidth: 300 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={400}>
                <Treemap data={data.items} dataKey="size" stroke="#0F1B33" isAnimationActive={false}
                  content={<CustomCell />} onClick={(node) => node?.symbol && nav(`/stock/${node.symbol}`)}>
                  <Tooltip content={<TooltipContent />} />
                </Treemap>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-text-secondary">
              <span>-%8</span>
              <div className="w-56 h-2 rounded" style={{background:"linear-gradient(90deg, #E5484D 0%, #7F3A48 30%, #2A3D66 50%, #388B5C 70%, #46C06F 100%)"}} />
              <span>+%8</span>
            </div>
          </>
        )
      ) : (
        <SectorGrid data={sectorData} nav={nav} />
      )}
    </div>
  );
}

function SectorGrid({ data, nav }) {
  if (!data || data.sectors.length === 0)
    return <div className="bg-bg-panel border border-bg-border rounded-xl p-16 text-center text-text-primary">Sektör verisi yok</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sector-grid">
      {data.sectors.map((s) => {
        const up = s.avg_pct >= 0;
        return (
          <div key={s.sector} data-testid={`sector-card-${s.sector}`}
            className={`bg-bg-panel border rounded-xl p-4 ${up ? "border-signal-up/40" : "border-signal-down/40"}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">{s.sector}</h3>
                <p className="text-[11px] text-text-secondary">{s.count} hisse · {s.up} yükseliş / {s.down} düşüş</p>
              </div>
              <div className={`text-xl font-mono font-bold ${up ? "text-signal-up" : "text-signal-down"}`}>
                {up ? "+" : ""}{s.avg_pct.toFixed(2)}%
              </div>
            </div>
            <div className="mt-3 space-y-1 text-xs">
              {s.top_gainer && (
                <div className="flex items-center justify-between hover:bg-bg-hover px-2 py-1 rounded cursor-pointer" onClick={() => nav(`/stock/${s.top_gainer.symbol}`)}>
                  <span className="text-signal-up">▲ {s.top_gainer.symbol}</span>
                  <span className="font-mono text-signal-up">+{s.top_gainer.pct_change.toFixed(2)}%</span>
                </div>
              )}
              {s.top_loser && s.top_loser.symbol !== s.top_gainer?.symbol && (
                <div className="flex items-center justify-between hover:bg-bg-hover px-2 py-1 rounded cursor-pointer" onClick={() => nav(`/stock/${s.top_loser.symbol}`)}>
                  <span className="text-signal-down">▼ {s.top_loser.symbol}</span>
                  <span className="font-mono text-signal-down">{s.top_loser.pct_change.toFixed(2)}%</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
